import { type Call, type Device } from '@twilio/voice-sdk'
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { useRevalidator } from 'react-router'
import { type action as callAction } from '#app/routes/resources/agent-call.ts'
import { type action as presenceAction } from '#app/routes/resources/agent-presence.ts'
import { type loader as tokenLoader } from '#app/routes/resources/agent-voice-token.ts'
import { type CallVia } from '#app/utils/support-center-api.server.ts'
import { SoftphoneCallBar } from './softphone-call-bar.tsx'
import { SoftphoneIncomingSheet } from './softphone-incoming-sheet.tsx'

/**
 * Browser softphone (Twilio Voice JS SDK) for a member linked to a
 * call-center agent. Mounted once in `root.tsx` so a call survives
 * navigation.
 *
 * Lifecycle
 *   enable()  — mic permission → token from /resources/agent-voice-token →
 *               `Device.register()` → heartbeat `softphone=true` every 30 s
 *               on /resources/agent-presence, so Support-center rings the
 *               browser first (`<Client>`) instead of the mobile.
 *   incoming  — `device.on('incoming')` → bottom sheet « Répondre / Refuser ».
 *               Unanswered after ~15 s, or refused, the call falls back to the
 *               agent's mobile, then to voicemail — nothing is lost.
 *   placeCall — POST /resources/agent-call; the API answers `softphone`
 *               (→ `device.connect(params)`) or `mobile` (→ the agent's phone
 *               rings, they press 1, the contact is dialed).
 *   drop      — a signalling drop (network change, right after a call…) puts
 *               the softphone in `reconnecting`: the SDK re-registers itself
 *               when its transport is back, and a guarded fallback loop
 *               (fresh token if needed, exponential backoff) covers the cases
 *               where it doesn't. After ~2½ min without success, or when the
 *               server refuses us (session gone, agent deactivated), the
 *               softphone stops for good — `error`, heartbeat `false` — and
 *               offers « Réessayer ».
 *   disable() — heartbeat `softphone=false`, `Device.destroy()`.
 *
 * The SDK is imported lazily in the browser only (it touches `window` at
 * import time). The opt-in is remembered per browser in localStorage so the
 * softphone comes back on the next visit — after a tap: browsers keep an
 * AudioContext created without user activation suspended, and the SDK's
 * ringtone plays through one, so registering straight from a page load would
 * ring silently (`pending` = waiting for that first tap).
 */

const STORAGE_KEY = 'plq:softphone'
const HEARTBEAT_MS = 30_000
/** Budget for one round-trip to our own resource routes. */
const REQUEST_TIMEOUT_MS = 10_000
/** Reconnect: 1 s, 2 s, 4 s… capped at 30 s, ≈ 2½ min in total. */
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000
const MAX_RECONNECT_ATTEMPTS = 8
/** Ask the SDK for `tokenWillExpire` this long before expiry — room to retry. */
const TOKEN_REFRESH_LEAD_MS = 120_000
const TOKEN_REFRESH_ATTEMPTS = 5
const TOKEN_REFRESH_RETRY_MS = 20_000
/**
 * After a call ends, wait for Twilio's end-of-call callbacks to reach the
 * API before re-reading presence for the header.
 */
const CALL_END_REFRESH_MS = 2_500

export type SoftphoneStatus =
	| 'off'
	/** Opted in, waiting for the first tap of the visit (autoplay policy). */
	| 'pending'
	| 'starting'
	| 'ready'
	/** Registration lost; the SDK and our fallback loop are re-registering. */
	| 'reconnecting'
	/** Stopped after a failure; `enable()` retries. */
	| 'error'

export type SoftphoneCall = {
	direction: 'inbound' | 'outbound'
	/** E.164 of the other party, when known. */
	number: string | null
	ticketId: number | null
	language: string | null
	/** ringing = inbound not yet answered; connecting = outbound not yet answered. */
	phase: 'ringing' | 'connecting' | 'active'
	/** `Date.now()` when the media was connected. */
	connectedAt: number | null
	muted: boolean
}

export type PlaceCallInput = {
	to?: string
	ticketId?: number
	via?: CallVia
}

export type PlaceCallResult =
	| { ok: true; via: 'softphone' | 'mobile' }
	| { ok: false; error: string }

export type Softphone = {
	/** The signed-in user is linked to an agent — the feature exists for them. */
	available: boolean
	status: SoftphoneStatus
	error: string | null
	call: SoftphoneCall | null
	enable: () => Promise<void>
	disable: () => Promise<void>
	accept: () => void
	reject: () => void
	hangup: () => void
	toggleMute: () => void
	placeCall: (input: PlaceCallInput) => Promise<PlaceCallResult>
}

const noop = () => {}
const UNAVAILABLE: Softphone = {
	available: false,
	status: 'off',
	error: null,
	call: null,
	enable: async () => {},
	disable: async () => {},
	accept: noop,
	reject: noop,
	hangup: noop,
	toggleMute: noop,
	placeCall: async () => ({
		ok: false,
		error: 'Aucun agent lié à ce compte.',
	}),
}

const SoftphoneContext = createContext<Softphone>(UNAVAILABLE)

export function useSoftphone(): Softphone {
	return useContext(SoftphoneContext)
}

// ---------------------------------------------------------------------------
// Server round-trips (plain fetch: a `useFetcher` would revalidate every
// loader on each 30 s heartbeat).
// ---------------------------------------------------------------------------

type TokenResponse =
	Awaited<ReturnType<typeof tokenLoader>> extends infer R
		? R extends { data: infer D }
			? D
			: R
		: never
type PresenceResponse =
	Awaited<ReturnType<typeof presenceAction>> extends infer R
		? R extends { data: infer D }
			? D
			: R
		: never
type CallResponse =
	Awaited<ReturnType<typeof callAction>> extends infer R
		? R extends { data: infer D }
			? D
			: R
		: never

/** A non-JSON answer, or a JSON refusal, from one of our resource routes. */
class ApiError extends Error {
	constructor(
		readonly httpStatus: number,
		message: string,
	) {
		super(message)
		this.name = 'ApiError'
	}
}

/**
 * 401 (session gone), 403 (account no longer linked) and 409 (agent
 * deactivated) won't fix themselves by retrying: the softphone must stop.
 */
function isRefusal(error: unknown): error is ApiError {
	return (
		error instanceof ApiError &&
		(error.httpStatus === 401 ||
			error.httpStatus === 403 ||
			error.httpStatus === 409)
	)
}

async function postForm<T extends { ok: boolean; error: string | null }>(
	url: string,
	fields: Record<string, string>,
	init: RequestInit = {},
): Promise<T & { httpStatus: number }> {
	const response = await fetch(url, {
		method: 'POST',
		headers: { 'content-type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams(fields),
		credentials: 'same-origin',
		signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
		...init,
	})
	let json: T
	try {
		json = (await response.json()) as T
	} catch {
		// A proxy error page, the maintenance page… anything but our JSON.
		throw new ApiError(
			response.status,
			`Le serveur n'a pas répondu correctement (HTTP ${response.status}).`,
		)
	}
	return { ...json, httpStatus: response.status }
}

async function fetchToken(): Promise<string> {
	const json = await postForm<TokenResponse>('/resources/agent-voice-token', {})
	if (!json.ok) throw new ApiError(json.httpStatus, json.error)
	return json.token
}

type HeartbeatOutcome = {
	/** The server refused the softphone for good (see `isRefusal`). */
	refused: string | null
}

async function postHeartbeat(
	connected: boolean,
	keepalive: boolean,
): Promise<HeartbeatOutcome> {
	try {
		const json = await postForm<PresenceResponse>(
			'/resources/agent-presence',
			{ softphone: connected ? 'true' : 'false' },
			{ keepalive },
		)
		if (!json.ok && isRefusal(new ApiError(json.httpStatus, json.error))) {
			return { refused: json.error }
		}
	} catch {
		// Network blip: the next beat will do; upstream forgets us after 90 s.
	}
	return { refused: null }
}

// Heartbeats are chained: two in flight at once (e.g. a `false` then a
// `true` on an SDK reconnect) could land out of order upstream and leave
// the API believing the browser is gone.
let heartbeatQueue: Promise<unknown> = Promise.resolve()

function sendHeartbeat(connected: boolean): Promise<HeartbeatOutcome> {
	const next = heartbeatQueue.then(() => postHeartbeat(connected, false))
	heartbeatQueue = next
	return next
}

/**
 * The tab is going away: tell the API right now, *not* behind the queue —
 * a promise continuation may never run once the page is torn down.
 * `keepalive` lets the browser finish the request after unload.
 */
function sendGoodbye() {
	void postHeartbeat(false, true)
}

function readStoredOptIn(): boolean {
	try {
		return window.localStorage.getItem(STORAGE_KEY) === 'on'
	} catch {
		return false
	}
}

function storeOptIn(on: boolean) {
	try {
		if (on) window.localStorage.setItem(STORAGE_KEY, 'on')
		else window.localStorage.removeItem(STORAGE_KEY)
	} catch {
		// private mode — the softphone just won't auto-start next time
	}
}

/**
 * Has this document seen a user gesture? Autoplay policies key on it: an
 * AudioContext created before the first tap stays suspended, and with it
 * the SDK's ringtone. Unknown (older browsers) counts as « not yet ».
 */
function hasUserActivation(): boolean {
	const activation = (
		navigator as Navigator & {
			userActivation?: { hasBeenActive: boolean }
		}
	).userActivation
	return activation?.hasBeenActive ?? false
}

/**
 * Resume the SDK's shared AudioContext; if the browser wants the call made
 * from inside a gesture (Safari), do it on the next tap or key press.
 */
function unlockAudio(DeviceCtor: typeof Device) {
	const context = DeviceCtor.audioContext
	if (!context || context.state !== 'suspended') return
	const resume = () => context.resume().catch(() => {})
	void resume().then(() => {
		if (context.state !== 'suspended') return
		const onGesture = () => {
			void resume()
			window.removeEventListener('pointerdown', onGesture, true)
			window.removeEventListener('keydown', onGesture, true)
		}
		window.addEventListener('pointerdown', onGesture, { capture: true })
		window.addEventListener('keydown', onGesture, { capture: true })
	})
}

function isTokenError(error: unknown): boolean {
	const code = (error as { code?: number } | null)?.code
	return code === 20101 || code === 31204 || code === 31205
}

function describeSdkError(error: unknown): string {
	if (error instanceof ApiError) return error.message
	const e = error as { code?: number; message?: string; name?: string }
	if (e?.name === 'NotAllowedError' || e?.code === 31208) {
		return 'Accès au micro refusé. Autorisez le micro pour ce site, puis réessayez.'
	}
	if (e?.name === 'NotFoundError') {
		return 'Aucun micro détecté sur cet appareil.'
	}
	if (e?.name === 'NotReadableError') {
		return 'Le micro est utilisé par une autre application.'
	}
	if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
		return 'Le serveur met trop de temps à répondre.'
	}
	if (e?.code === 31005 || e?.code === 31000 || e?.code === 31009) {
		return "Connexion au service d'appels impossible (réseau ou configuration Twilio)."
	}
	if (isTokenError(e)) {
		return "Jeton d'appel invalide ou expiré."
	}
	return e?.message ?? 'Erreur du softphone.'
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function SoftphoneProvider({
	available,
	children,
}: {
	available: boolean
	children: ReactNode
}) {
	const [status, setStatus] = useState<SoftphoneStatus>('off')
	const [error, setError] = useState<string | null>(null)
	const [call, setCall] = useState<SoftphoneCall | null>(null)
	const deviceRef = useRef<Device | null>(null)
	const callRef = useRef<Call | null>(null)
	const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	/** Bumped by every teardown so an `enable()` still awaiting bails out. */
	const generationRef = useRef(0)
	/** Synchronous re-entrancy guard: `deviceRef` is only set after 3 awaits. */
	const startingRef = useRef(false)
	/** The Device holds a token the server rejected — refresh before re-registering. */
	const tokenStaleRef = useRef(false)
	/** Removes the « first tap » listeners armed while `pending`. */
	const cancelPendingRef = useRef<(() => void) | null>(null)
	const revalidator = useRevalidator()
	const revalidate = revalidator.revalidate

	const stopHeartbeat = useCallback(() => {
		if (heartbeatRef.current) clearInterval(heartbeatRef.current)
		heartbeatRef.current = null
	}, [])

	const clearReconnect = useCallback(() => {
		if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
		reconnectTimerRef.current = null
	}, [])

	const cancelPending = useCallback(() => {
		cancelPendingRef.current?.()
		cancelPendingRef.current = null
	}, [])

	/** Release everything. Returns whether a Device existed (→ tell the API). */
	const teardown = useCallback((): boolean => {
		generationRef.current++
		startingRef.current = false
		cancelPending()
		stopHeartbeat()
		clearReconnect()
		callRef.current?.disconnect()
		callRef.current = null
		const device = deviceRef.current
		deviceRef.current = null
		device?.destroy()
		setCall(null)
		return device !== null
	}, [cancelPending, stopHeartbeat, clearReconnect])

	/** Stop for good with a message; « Réessayer » goes through `enable()`. */
	const failTerminal = useCallback(
		(message: string) => {
			if (teardown()) void sendHeartbeat(false)
			setError(message)
			setStatus('error')
		},
		[teardown],
	)

	const startHeartbeat = useCallback(() => {
		stopHeartbeat()
		const beat = () =>
			void sendHeartbeat(true).then(({ refused }) => {
				// Still ours (not disabled meanwhile) and refused for good.
				if (refused && heartbeatRef.current) failTerminal(refused)
			})
		beat()
		heartbeatRef.current = setInterval(beat, HEARTBEAT_MS)
	}, [stopHeartbeat, failTerminal])

	const clearCall = useCallback(() => {
		// Our own reject()/hangup() and the SDK's matching event both land
		// here: only the first one revalidates.
		if (!callRef.current) return
		callRef.current = null
		setCall(null)
		// Presence / call log changed upstream — repaint the header dot & lists.
		void revalidate()
		// The API learns the outcome from Twilio's callbacks a moment after the
		// SDK's event. Once they have landed, a heartbeat primes the header's
		// presence cache with the fresh row and a second revalidation paints it,
		// so the dot leaves « ringing » / « on call » without a reload.
		window.setTimeout(() => {
			if (!deviceRef.current) return
			void sendHeartbeat(true).then(() => revalidate())
		}, CALL_END_REFRESH_MS)
	}, [revalidate])

	const wireCall = useCallback(
		(twilioCall: Call, initial: SoftphoneCall) => {
			callRef.current = twilioCall
			setCall(initial)
			twilioCall.on('accept', () => {
				// For an outbound call this is the *browser* leg being answered
				// (upstream dials the contact without `answerOnBridge`), so the
				// timer starts while the contact may still be ringing.
				setCall((c) =>
					c ? { ...c, phase: 'active', connectedAt: Date.now() } : c,
				)
			})
			twilioCall.on('mute', (muted: boolean) => {
				setCall((c) => (c ? { ...c, muted } : c))
			})
			for (const ev of ['disconnect', 'cancel', 'reject'] as const) {
				twilioCall.on(ev, clearCall)
			}
			twilioCall.on('error', (e: unknown) => {
				setError(describeSdkError(e))
				// Only a call the SDK gave up on is over; an in-call glitch isn't.
				if (twilioCall.status() === 'closed') clearCall()
			})
		},
		[clearCall],
	)

	/** `tokenWillExpire`: fetch a new token, with patience for a bad network. */
	const refreshToken = useCallback(
		async (device: Device) => {
			for (let attempt = 1; attempt <= TOKEN_REFRESH_ATTEMPTS; attempt++) {
				if (deviceRef.current !== device) return
				try {
					const token = await fetchToken()
					if (deviceRef.current !== device) return
					device.updateToken(token)
					tokenStaleRef.current = false
					return
				} catch (e) {
					if (deviceRef.current !== device) return
					if (isRefusal(e)) {
						failTerminal(describeSdkError(e))
						return
					}
					await sleep(TOKEN_REFRESH_RETRY_MS)
				}
			}
			// The current token will lapse; when the gateway drops us, the
			// reconnect loop fetches a fresh one before re-registering.
			tokenStaleRef.current = true
		},
		[failTerminal],
	)

	const enable = useCallback(async () => {
		if (!available || deviceRef.current || startingRef.current) return
		startingRef.current = true
		cancelPending()
		const generation = ++generationRef.current
		const live = () => generation === generationRef.current
		setStatus('starting')
		setError(null)
		try {
			// Ask for the mic now: a permission prompt in the middle of a ringing
			// call would eat the 15 s the browser has before the mobile fallback.
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			stream.getTracks().forEach((t) => t.stop())
			if (!live()) return

			const token = await fetchToken()
			if (!live()) return
			const { Device: DeviceCtor, Call: CallCtor } =
				await import('@twilio/voice-sdk')
			if (!live()) return

			const device = new DeviceCtor(token, {
				codecPreferences: [CallCtor.Codec.Opus, CallCtor.Codec.PCMU],
				closeProtection: 'Un appel est en cours dans cet onglet.',
				logLevel: 'warn',
				tokenRefreshMs: TOKEN_REFRESH_LEAD_MS,
			})
			deviceRef.current = device
			tokenStaleRef.current = false

			// Fallback re-registration. The SDK re-registers by itself once its
			// transport reconnects (`register()` even throws if we race it), so
			// every attempt first checks the Device's state; a token the
			// gateway rejected is replaced first. Bounded: after
			// MAX_RECONNECT_ATTEMPTS the softphone stops rather than pretending.
			let attempts = 0
			const scheduleReconnect = (delayMs: number) => {
				clearReconnect()
				reconnectTimerRef.current = setTimeout(
					() => void attemptReconnect(),
					delayMs,
				)
			}
			const attemptReconnect = async () => {
				reconnectTimerRef.current = null
				if (deviceRef.current !== device) return
				if (device.state === DeviceCtor.State.Registered) {
					attempts = 0
					return
				}
				const attempt = ++attempts
				if (attempt > MAX_RECONNECT_ATTEMPTS) {
					failTerminal(
						"Impossible de rétablir la connexion au service d'appels. Vérifiez le réseau, puis réessayez.",
					)
					return
				}
				const backoff = Math.min(
					RECONNECT_MAX_MS,
					RECONNECT_BASE_MS * 2 ** (attempt - 1),
				)
				// Registering: the SDK is on it — check back later.
				if (device.state !== DeviceCtor.State.Unregistered) {
					scheduleReconnect(backoff)
					return
				}
				try {
					if (tokenStaleRef.current) {
						const fresh = await fetchToken()
						if (deviceRef.current !== device) return
						device.updateToken(fresh)
						tokenStaleRef.current = false
						if (device.state !== DeviceCtor.State.Unregistered) {
							scheduleReconnect(backoff)
							return
						}
					}
					await device.register()
					// `registered` resets the counters.
				} catch (e) {
					if (deviceRef.current !== device) return
					if (isRefusal(e)) {
						failTerminal(describeSdkError(e))
						return
					}
					if (isTokenError(e)) tokenStaleRef.current = true
					scheduleReconnect(backoff)
				}
			}

			device.on('registered', () => {
				if (deviceRef.current !== device) return
				attempts = 0
				clearReconnect()
				setStatus('ready')
				setError(null)
				startHeartbeat()
			})
			device.on('unregistered', () => {
				// Our own unregister()/destroy(): deviceRef is already cleared.
				// Otherwise a signalling drop — typically right after a call or
				// on a network change. Keep the heartbeat: a dead `<Client>`
				// fails fast upstream and the call falls back to the mobile.
				if (deviceRef.current !== device) return
				setStatus((s) =>
					s === 'ready' || s === 'reconnecting' ? 'reconnecting' : s,
				)
				scheduleReconnect(RECONNECT_BASE_MS)
			})
			device.on('destroyed', () => {
				// Only the SDK destroys behind our back: it listens to `pagehide`
				// itself. If the page comes back from the bfcache, `pageshow`
				// re-enables; until then we hold no dead Device.
				if (deviceRef.current !== device) return
				deviceRef.current = null
				stopHeartbeat()
				clearReconnect()
				callRef.current = null
				setCall(null)
				setStatus('off')
			})
			device.on('error', (e: unknown) => {
				if (deviceRef.current !== device) return
				if (isTokenError(e)) tokenStaleRef.current = true
				setError(describeSdkError(e))
				// The initial registration surfaces through register()'s
				// rejection below; later ones through the reconnect loop.
			})
			device.on('tokenWillExpire', () => void refreshToken(device))
			device.on('incoming', (twilioCall: Call) => {
				if (callRef.current) {
					// Already on a call — let Support-center fall back to the mobile.
					twilioCall.reject()
					return
				}
				const params = twilioCall.customParameters
				const ticketId = Number(params.get('ticketId'))
				wireCall(twilioCall, {
					direction: 'inbound',
					number: params.get('from') ?? twilioCall.parameters.From ?? null,
					ticketId:
						Number.isInteger(ticketId) && ticketId > 0 ? ticketId : null,
					language: params.get('language') ?? null,
					phase: 'ringing',
					connectedAt: null,
					muted: false,
				})
			})

			await device.register()
			if (deviceRef.current !== device) return
			storeOptIn(true)
			unlockAudio(DeviceCtor)
		} catch (e) {
			if (!live()) return
			teardown()
			setError(describeSdkError(e))
			setStatus('error')
			// Don't walk into the same failure on the next page load.
			storeOptIn(false)
		} finally {
			if (live()) startingRef.current = false
		}
	}, [
		available,
		cancelPending,
		clearReconnect,
		failTerminal,
		refreshToken,
		startHeartbeat,
		stopHeartbeat,
		teardown,
		wireCall,
	])

	const disable = useCallback(async () => {
		storeOptIn(false)
		const hadDevice = teardown()
		if (hadDevice) await sendHeartbeat(false)
		setStatus('off')
		setError(null)
	}, [teardown])

	const accept = useCallback(() => {
		callRef.current?.accept()
	}, [])

	const reject = useCallback(() => {
		callRef.current?.reject()
		clearCall()
	}, [clearCall])

	const hangup = useCallback(() => {
		callRef.current?.disconnect()
		clearCall()
	}, [clearCall])

	const toggleMute = useCallback(() => {
		const c = callRef.current
		if (c) c.mute(!c.isMuted())
	}, [])

	const placeCall = useCallback(
		async (input: PlaceCallInput): Promise<PlaceCallResult> => {
			if (!available)
				return {
					ok: false,
					error: 'Aucun agent lié à ce compte.',
				}
			if (callRef.current)
				return { ok: false, error: 'Un appel est déjà en cours.' }

			const fields: Record<string, string> = {}
			if (input.to) fields.to = input.to
			if (input.ticketId) fields.ticketId = String(input.ticketId)
			// Without a registered device the API must not pick the softphone.
			fields.via =
				input.via ??
				(deviceRef.current && status === 'ready' ? 'auto' : 'mobile')

			let json: CallResponse & { httpStatus: number }
			try {
				json = await postForm<CallResponse>('/resources/agent-call', fields)
			} catch (e) {
				return {
					ok: false,
					error:
						e instanceof ApiError
							? e.message
							: "Impossible de joindre le centre d'appel.",
				}
			}
			if (!json.ok) return { ok: false, error: json.error }

			if (json.result.via === 'mobile') return { ok: true, via: 'mobile' }

			const device = deviceRef.current
			if (!device) {
				return { ok: false, error: "Le softphone n'est pas démarré." }
			}
			try {
				const twilioCall = await device.connect({
					params: json.result.connectParams,
				})
				wireCall(twilioCall, {
					direction: 'outbound',
					number: json.result.to,
					ticketId: json.result.parentTicketId,
					language: null,
					phase: 'connecting',
					connectedAt: null,
					muted: false,
				})
				return { ok: true, via: 'softphone' }
			} catch (e) {
				return { ok: false, error: describeSdkError(e) }
			}
		},
		[available, status, wireCall],
	)

	// External subscriptions: auto-start when opted in (after the visit's
	// first tap), tell the API when the tab goes away, come back after a
	// bfcache restore, and release everything on unmount.
	useEffect(() => {
		if (!available) return

		const resume = () => {
			if (!deviceRef.current && !startingRef.current) void enable()
		}
		const armPending = () => {
			cancelPendingRef.current?.()
			const onGesture = () => {
				cancelPendingRef.current?.()
				cancelPendingRef.current = null
				resume()
			}
			window.addEventListener('pointerdown', onGesture, { capture: true })
			window.addEventListener('keydown', onGesture, { capture: true })
			cancelPendingRef.current = () => {
				window.removeEventListener('pointerdown', onGesture, true)
				window.removeEventListener('keydown', onGesture, true)
			}
			setStatus('pending')
		}
		const startIfOptedIn = () => {
			if (!readStoredOptIn() || deviceRef.current || startingRef.current) return
			if (hasUserActivation()) resume()
			else armPending()
		}

		startIfOptedIn()
		const onPageHide = () => {
			if (deviceRef.current) sendGoodbye()
		}
		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) startIfOptedIn()
		}
		window.addEventListener('pagehide', onPageHide)
		window.addEventListener('pageshow', onPageShow)
		return () => {
			window.removeEventListener('pagehide', onPageHide)
			window.removeEventListener('pageshow', onPageShow)
			if (teardown()) void sendHeartbeat(false)
			setStatus('off')
			setError(null)
		}
		// `enable` / `teardown` are stable (memoised on `available` only).
	}, [available, enable, teardown])

	const value = useMemo<Softphone>(
		() =>
			available
				? {
						available,
						status,
						error,
						call,
						enable,
						disable,
						accept,
						reject,
						hangup,
						toggleMute,
						placeCall,
					}
				: UNAVAILABLE,
		[
			available,
			status,
			error,
			call,
			enable,
			disable,
			accept,
			reject,
			hangup,
			toggleMute,
			placeCall,
		],
	)

	return (
		<SoftphoneContext.Provider value={value}>
			{children}
			{available ? (
				<>
					<SoftphoneIncomingSheet />
					<SoftphoneCallBar />
				</>
			) : null}
		</SoftphoneContext.Provider>
	)
}
